import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, PhoneOff, Search, Workflow, TrendingUp, BarChart3 } from 'lucide-react';
import { useT } from '../i18n';

/* =================================================================
   Challenges — Problem-first narrative scroll.

   This section answers ONE question only:
   "What problems does the platform actually solve for me?"

   It deliberately does NOT repeat the service catalog above.
   Each card is a real operator pain point (eyebrow as a question)
   followed by a solution-oriented headline and a subtle pointer to
   the Kitxens capability that solves it.

   The premium visual style, spacing and animations are preserved.
   This is a messaging evolution, not a redesign.
   ================================================================= */

const CARD_ICONS = [PhoneOff, Search, Workflow, TrendingUp, BarChart3];

export default function Challenges() {
  const { t } = useT();
  const cards = t('challengeCards');
  const solutionLabel = t('challenges.solutionLabel');

  return (
    <section
      className="relative py-20 lg:py-28 bg-white overflow-hidden"
      id="problems"
      data-testid="problems-block"
    >
      {/* Subtle dot pattern */}
      <div
        aria-hidden="true"
        className="absolute inset-0 opacity-[0.05] pointer-events-none"
        style={{
          backgroundImage:
            'radial-gradient(circle at 20% 30%, #F77F11 0px, transparent 1.5px), radial-gradient(circle at 80% 70%, #F77F11 0px, transparent 1.5px)',
          backgroundSize: '120px 120px, 160px 160px',
        }}
      />

      <div className="relative max-w-[1280px] mx-auto px-6 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.5 }}
          className="max-w-3xl"
        >
          <p
            className="kx-eyebrow mb-4"
            data-testid="problems-eyebrow"
          >
            {t('challenges.eyebrow')}
          </p>
          <h2 className="text-3xl sm:text-4xl lg:text-[44px] font-bold text-neutral-900 tracking-[-0.02em] leading-[1.1]">
            {t('challenges.title')}{' '}
            <span className="bg-gradient-to-r from-[#F77F11] to-[#ff9b4a] bg-clip-text text-transparent pb-[0.12em] -mb-[0.12em]">
              {t('challenges.titleAccent')}
            </span>
          </h2>
          <p
            className="mt-5 text-neutral-600 text-base sm:text-[17px] leading-relaxed max-w-2xl"
            data-testid="problems-intro"
          >
            {t('challenges.intro')}
          </p>
        </motion.div>

        {/* 5 problem cards, same grid rhythm used elsewhere */}
        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
          {cards.map((c, i) => {
            const I = CARD_ICONS[i] || Search;
            return (
              <motion.div
                key={c.question}
                initial={{ opacity: 0, y: 25 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-50px' }}
                transition={{ duration: 0.45, delay: i * 0.07 }}
                whileHover={{ y: -4 }}
                data-testid={`problem-card-${i}`}
              >
                <Link
                  to={c.solutionHref}
                  className="group block h-full rounded-2xl border border-neutral-200 bg-white p-6 hover:border-[#F77F11] hover:shadow-[0_28px_70px_-30px_rgba(247,127,17,0.4)] transition-all duration-300 flex flex-col"
                  data-testid={`problem-link-${i}`}
                >
                  <span className="inline-flex w-11 h-11 rounded-xl bg-orange-50 items-center justify-center group-hover:bg-[#F77F11] transition-colors duration-300">
                    <I className="w-5 h-5 text-[#F77F11] group-hover:text-white transition-colors duration-300" />
                  </span>

                  {/* The hook: the question, in italics, like an operator's inner monologue */}
                  <p
                    className="mt-5 text-[12.5px] font-medium text-neutral-500 italic tracking-[-0.005em]"
                    data-testid={`problem-question-${i}`}
                  >
                    {c.question}
                  </p>

                  {/* The headline: a confident, action-led answer */}
                  <h3
                    className="mt-1.5 text-neutral-900 font-bold text-[18px] tracking-[-0.01em] leading-snug"
                    data-testid={`problem-title-${i}`}
                  >
                    {c.title}
                  </h3>

                  <p className="mt-2.5 text-neutral-600 text-[15px] leading-relaxed flex-1">
                    {c.description}
                  </p>

                  {/* Subtle solution pointer — never feels like a sales pitch */}
                  <div className="mt-5 pt-4 border-t border-neutral-100">
                    <p className="text-[9px] tracking-[0.22em] uppercase font-bold text-neutral-400 mb-1.5">
                      {solutionLabel}
                    </p>
                    <span
                      className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-[#F77F11] bg-orange-50 group-hover:bg-orange-100/80 border border-orange-200 rounded-full px-3 py-1 transition-colors whitespace-nowrap"
                      data-testid={`problem-solution-${i}`}
                    >
                      <span>{c.solution}</span>
                      <ArrowRight className="w-3 h-3" />
                    </span>
                  </div>
                </Link>
              </motion.div>
            );
          })}
        </div>

        {/* Soft close — keeps the original "Free Audit" hand-off without selling another service */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.45 }}
          className="mt-14 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-5 rounded-2xl border border-neutral-200 bg-neutral-50/60 px-6 py-5"
        >
          <p className="text-[15px] sm:text-base font-semibold text-neutral-900 tracking-[-0.005em]">
            {t('challenges.notSure')}
          </p>
          <a
            href="#audit"
            className="inline-flex items-center gap-2 bg-[#F77F11] hover:bg-[#d8651a] text-white px-5 py-3 rounded-full font-semibold text-[14.5px] transition-colors shrink-0"
            data-testid="problems-audit-cta"
          >
            {t('challenges.auditCta')} <ArrowRight className="w-4 h-4" />
          </a>
        </motion.div>
      </div>
    </section>
  );
}
